import { User } from '@/store/api/userApi';
import {
  AddCircle as AddIcon,
  Business as BusinessIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  Box,
  Drawer,
  Fade,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Images from '../../../assets/Images';
import { useGetCurrentUserQuery } from '@/store/api/userApi'; 
import {
  getMenuItemsForRole,
  NavigationItem,
} from '../../../utils/menuRoleUtils';
import styles from './sidebar.module.scss';
// Extend the User interface to include is_superuser property
interface ExtendedUser extends User {
  is_superuser?: boolean;
}
import {
  useGetAuth0MeQuery,
} from '@/store/api/userApi';
import { getDocumentUrl } from '@/services/documentService';

interface SidebarProps {
  drawerWidth: number;
  open: boolean;
  onClose: () => void;
  variant: 'permanent' | 'persistent' | 'temporary';
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

  // Menu navigation array - Updated to use Material-UI icons consistently
  const menuNavigation: NavigationItem[] = [
    {
      title: 'Dashboard',
      path: '/dashboard',
      icon: <DashboardIcon />,
      icon_size: 14,
    },
    {
      title: 'Organization',
      path: '/admin/organization',
      icon: <BusinessIcon />,
      icon_size: 14,
      children: [
        {
          title: 'Organizations',
          path: '/admin/organization',
          icon: <BusinessIcon />,
          icon_size: 14,
        },
        {
          title: 'Organization Admin Users',
          path: '/admin/organization-admin-users',
          icon: <GroupIcon />,
          icon_size: 14,
        },
      ],
    },
    {
      title: 'Global Configuration',
      path: '/admin/global-config',
      icon: <SettingsIcon />,
      icon_size: 14,
    },
    {
      title: 'Profile',
      path: '/profile',
      icon: <PersonIcon />,
      icon_size: 14,
    },
  ];

interface SidebarProps {
  drawerWidth: number;
  open: boolean;
  onClose: () => void;
  variant: 'permanent' | 'persistent' | 'temporary';
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  drawerWidth,
  open,
  onClose,
  variant,
  collapsed = false,
  onToggleCollapse,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: currentUser, isLoading: userLoading } = useGetCurrentUserQuery();
  const { data: auth0MeData, isLoading: auth0Loading } = useGetAuth0MeQuery();

  const [activeMainMenu, setActiveMainMenu] = useState('Dashboard');
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [filteredMenu, setFilteredMenu] = useState<NavigationItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const collapsedWidth = 64;
  const currentWidth = collapsed ? collapsedWidth : drawerWidth;

  // 1) Fetch logo URL from API and memoize it; null if not available or blank
  const [apiLogo, setApiLogo] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchLogo = async () => {
      const logoId =
        auth0MeData?.user?.company_details?.profile?.logo ??
        auth0MeData?.user?.company_details?.profile?.logo_url;

      if (!logoId) {
        if (!cancelled) setApiLogo(null);
        return;
      }

      try {
        const url = await getDocumentUrl(logoId);
        if (!cancelled) {
          setApiLogo(url || null);
        }
      } catch (err) {
        console.error('Logo fetch failed:', err);
        if (!cancelled) setApiLogo(null);
      }
    };

    fetchLogo();

    return () => {
      cancelled = true;
    };
  }, [auth0MeData]);

  // 2) Final image source; null means "not decided", so render placeholder only
  const [logoSrc, setLogoSrc] = useState<string | null>(null);

  // 3) Preload utility
  const preload = (src: string) =>
    new Promise<boolean>((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = src;
    });

  // 4) Decide final logo strictly after preload; never render fallback before this
  useEffect(() => {
    let canceled = false;
    
    // Skip if still loading basic user info
    if (auth0Loading) return;

    const decideLogo = async () => {
      // If we have an API logo URL, preload it
      if (apiLogo) {
        const ok = await preload(apiLogo);
        if (!canceled) {
          setLogoSrc(ok ? apiLogo : Images.LOGO);
        }
      } else {
        // Check if there's a logo ID in the data that we're still waiting to fetch
        const logoId =
          auth0MeData?.user?.company_details?.profile?.logo ??
          auth0MeData?.user?.company_details?.profile?.logo_url;

        // If there's a logo ID but apiLogo is still null, wait for the fetch to complete
        if (logoId && !apiLogo) {
          return;
        }

        // If no logo ID exists at all, or fetching already resulted in null
        if (!canceled) {
          setLogoSrc(Images.LOGO);
        }
      }
    };

    decideLogo();
    
    return () => {
      canceled = true;
    };
  }, [apiLogo, auth0Loading, auth0MeData]);

  useEffect(() => {
    return () => {
      if (apiLogo) {
        URL.revokeObjectURL(apiLogo);
      }
    };
  }, [apiLogo]);

  // --------- Filter menu items based on user role ----------
  useEffect(() => {
    setIsLoading(true);
    if (!userLoading && currentUser) {
      const userWithSuperuser = {
        ...currentUser,
        is_superuser: (currentUser as ExtendedUser).is_superuser ?? false,
      };
      const filtered = getMenuItemsForRole(menuNavigation, userWithSuperuser);
      setFilteredMenu(filtered);
      setIsLoading(false);
    } else if (!userLoading && !currentUser) {
      setFilteredMenu(menuNavigation);
      setIsLoading(false);
    }
  }, [currentUser, userLoading]);

  // Collapse submenus when sidebar is collapsed
  useEffect(() => {
    if (collapsed) setExpandedMenus([]);
  }, [collapsed]);

  // Handle menu item hover for tooltips
  const handleMenuItemMouseEnter = (title: string) => {
    if (collapsed) {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = setTimeout(() => setHoveredItem(title), 200);
    }
  };
  const handleMenuItemMouseLeave = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setHoveredItem(null);
  };

  const handleNavigate = (path: string, title: string) => {
    navigate(path);
    if (variant === 'temporary') onClose();
    setActiveMainMenu(title);
  };

  const toggleSubmenu = (title: string) => {
    if (collapsed) return;
    setExpandedMenus((prev) => (prev.includes(title) ? [] : [title]));
  };

  const isMenuExpanded = (title: string) =>
    expandedMenus.includes(title) && !collapsed;

  const handleCollapseToggle = () => {
    if (onToggleCollapse) onToggleCollapse();
    else console.warn('onToggleCollapse prop is not provided');
  };

  const renderMenuItem = (item: NavigationItem) => {
    const isActive = activeMainMenu === item.title;
    const hasChildren = !!item.children && item.children.length > 0;
    // Using hoveredItem in the component logic
    const isItemHovered = hoveredItem === item.title;

    return (
      <li
        className={`${styles.menuItem} ${isActive ? 'active' : ''} ${hasChildren ? styles.hasChildren : ''}`}
        key={item.path}
        onMouseEnter={() => handleMenuItemMouseEnter(item.title)}
        onMouseLeave={handleMenuItemMouseLeave}
      >
        {collapsed ? (
          <Tooltip
            title={item.title}
            placement="right"
            arrow
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 200 }}
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'rgba(0, 0, 0, 0.9)',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  marginLeft: '8px',
                },
              },
              arrow: { sx: { color: 'rgba(0, 0, 0, 0.9)' } },
            }}
            open={isItemHovered}
          >
            <Box
              component="a"
              onClick={() => {
                if (hasChildren) toggleSubmenu(item.title);
                else handleNavigate(item.path, item.title);
              }}
              className={collapsed ? styles.collapsedMenuItem : ''}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                justifyContent: 'center',
                borderRadius: '8px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  transform: 'scale(1.05)',
                },
              }}
            >
              <span className="menu-icon" style={{
                color: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px'
              }}>
                <Box sx={{
                  color: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '& svg': {
                    width: '18px',
                    height: '18px'
                  }
                }}>
                  {item.icon}
                </Box>
              </span>
            </Box>
          </Tooltip>
        ) : (
          <a
            onClick={() => {
              if (hasChildren) toggleSubmenu(item.title);
              else handleNavigate(item.path, item.title);
            }}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              padding: '12px 16px',
              borderRadius: '8px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <span className="menu-icon" style={{
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              marginRight: '16px'
            }}>
              <Box sx={{
                color: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& svg': {
                  width: '18px',
                  height: '18px'
                }
              }}>
                {item.icon}
              </Box>
            </span>
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                fontWeight: isActive ? 500 : 400,
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                opacity: collapsed ? 0 : 1,
                transform: collapsed ? 'translateX(-10px)' : 'translateX(0)',
              }}
            >
              {item.title}
            </Typography>
            {hasChildren && !collapsed && (
              <Box className={styles.chevron}>
                {isMenuExpanded(item.title) ? (
                  <ChevronLeftIcon fontSize="small" />
                ) : (
                  <ChevronRightIcon fontSize="small" />
                )}
              </Box>
            )}
          </a>
        )}
      </li>
    );
  };

  const drawerContent = (
    <Box
      className={styles.drawerContainer}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        mt:10.3,
      }}
    >
      {/* Header section - logo moved to main header; this box now only holds the Add Asset button */}
      <Box className={`${styles.addAssetHeader} ${collapsed ? 'collapsed' : ''}`}>
        {!collapsed &&
          filteredMenu.some((item) => item.title === 'Assets Listing') && (
            <Box
              component="button"
              className="button button__solid--primary"
              onClick={() => navigate('/assets/create')}
              sx={{
                marginTop: '15px',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                opacity: collapsed ? 0 : 1,
                transform: collapsed ? 'scale(0.8)' : 'scale(1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AddIcon sx={{ width: '14px', height: '14px', marginRight: '8px' }} />
              <span>Add Asset</span>
            </Box>
          )}
        {collapsed &&
          filteredMenu.some((item) => item.title === 'Assets Listing') && (
            <Tooltip title="Add Asset" placement="right">
              <Box
                component="button"
                className="button button__solid--primary"
                onClick={() => navigate('/assets/create')}
                sx={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  padding: 0,
                  marginTop: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 'auto',
                  '& span': { display: 'none' },
                }}
              >
                <AddIcon sx={{ width: '16px', height: '16px' }} />
              </Box>
            </Tooltip>
          )}
      </Box>

      {/* Menu navigation */}
      <Box
        className={`${styles.menuScrollWrapper} ${collapsed ? 'collapsed' : ''}`}
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Box className={styles.menuWrapper}>
          {isLoading ? (
            <Box className={styles.loadingMenu}>
              <Typography variant="body2" color="text.secondary">
                {collapsed ? '...' : 'Loading menu...'}
              </Typography>
            </Box>
          ) : (
            <Box
              component="ul"
              className={`${styles.mainMenu} ${collapsed ? 'collapsed' : ''}`}
              sx={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {filteredMenu.map((item) => (
                <React.Fragment key={item.path}>
                  {renderMenuItem(item)}
                  {item.children &&
                    item.children.length > 0 &&
                    isMenuExpanded(item.title) && (
                      <Box
                        component="li"
                        className={styles.subMenuWrapper}
                        sx={{
                          overflow: 'hidden',
                          animation: 'slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '@keyframes slideDown': {
                            from: { maxHeight: 0, opacity: 0 },
                            to: { maxHeight: '500px', opacity: 1 },
                          },
                        }}
                      >
                        <Box
                          component="ul"
                          className={styles.subMenu}
                          sx={{
                            listStyle: 'none',
                            padding: collapsed ? '8px 0' : '8px 0 8px 48px',
                            margin: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                            borderRadius: '0 0 8px 8px',
                          }}
                        >
                          {item.children.map((child) => (
                            <Box
                              component="li"
                              key={child.path}
                              className={
                                activeMainMenu === child.title ? 'active' : ''
                              }
                              sx={{
                                marginBottom: '2px',
                                '&:last-child': { marginBottom: 0 },
                                '&.active a': {
                                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                  color: '#1976d2',
                                  fontWeight: 500,
                                },
                              }}
                              onMouseEnter={() =>
                                collapsed && handleMenuItemMouseEnter(child.title)
                              }
                              onMouseLeave={
                                collapsed ? handleMenuItemMouseLeave : undefined
                              }
                            >
                              <Tooltip
                                title={child.title}
                                placement="right"
                                arrow
                                TransitionComponent={Fade}
                                TransitionProps={{ timeout: 200 }}
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      bgcolor: 'rgba(0, 0, 0, 0.9)',
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                      marginLeft: collapsed ? '8px' : '0',
                                    },
                                  },
                                  arrow: { sx: { color: 'rgba(0, 0, 0, 0.9)' } },
                                }}
                                disableHoverListener={!collapsed}
                              >
                                <Box
                                  component="a"
                                  onClick={() =>
                                    handleNavigate(child.path, child.title)
                                  }
                                  sx={{
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: collapsed ? '8px' : '8px 16px',
                                    fontSize: '13px',
                                    color: 'rgba(0, 0, 0, 0.7)',
                                    borderRadius: '6px',
                                    textDecoration: 'none',
                                    transition:
                                      'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                      color: 'rgba(0, 0, 0, 0.87)',
                                    },
                                    justifyContent: collapsed
                                      ? 'center'
                                      : 'flex-start',
                                  }}
                                >
                                  <Box
                                    component="span"
                                    className="menu-icon"
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      marginRight: collapsed ? 0 : '12px',
                                      width: '20px',
                                      height: '20px',
                                      color: theme.palette.primary.main
                                    }}
                                  >
                                    <Box sx={{
                                      color: theme.palette.primary.main,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      '& svg': {
                                        width: '16px',
                                        height: '16px'
                                      }
                                    }}>
                                      {child.icon}
                                    </Box>
                                  </Box>
                                  {!collapsed && (
                                    <Typography variant="body2">
                                      {child.title}
                                    </Typography>
                                  )}
                                </Box>
                              </Tooltip>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                </React.Fragment>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          padding: '16px',
          marginTop: 'auto',
          borderTop: '1px solid rgba(0, 0, 0, 0.05)',
          display: 'flex',
          justifyContent: 'center',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Tooltip
          title={collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          placement="right"
          arrow
        >
          <IconButton
            onClick={handleCollapseToggle}
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                transform: 'scale(1.05)',
              },
              '&:active': { transform: 'scale(0.95)' },
            }}
            size="small"
          >
            {collapsed ? (
              <ChevronRightIcon fontSize="small" />
            ) : (
              <ChevronLeftIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* App version */}
      <Fade in={!collapsed} timeout={400}>
        <Box
          sx={{
            padding: '16px',
            borderTop: '1px solid rgba(0, 0, 0, 0.05)',
            opacity: collapsed ? 0 : 1,
            transform: collapsed ? 'translateY(10px)' : 'translateY(0)',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', fontWeight: 500 }}
          >
            Version 1.0.0
          </Typography>
        </Box>
      </Fade>
    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }} 
      sx={{
        width: currentWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: currentWidth,
          boxSizing: 'border-box',
          border: 'none',
          boxShadow: variant === 'temporary' ? theme.shadows[8] : 'none',
          overflowX: 'hidden',
          transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;